import React, { useState, useEffect, Fragment, useContext } from 'react';
import { AppContext } from 'components/AppDesktop';

import { useApi, obtenerCuentas, establecerInstanciaActiva } from 'api';
import isArray from 'lodash/isArray';
import { SuspenseLoading } from 'Routes';

const cuentaInitialState = {
  init: false,
  loaded: false,
  logged: false,
  permisos: [],
};

const cuentaContextInitialState = {
  cuenta: {},
  actualizaCuenta: () => { },
  tieneCuentaPermisos: () => { },
  wipeAllData: () => { },
  recargaCuenta: () => { },
  seleccionaCliente: () => { },
  solicitaCambioCliente: () => { },
};

// Persistencia (por usuario) del cliente seleccionado cuando la cuenta tiene varios.
const CLIENTES_SELECCIONADOS_KEY = 'escolastia.clientesSeleccionados';

const leeClienteSeleccionadoId = (usuarioId) => {
  try {
    const map = JSON.parse(window.localStorage.getItem(CLIENTES_SELECCIONADOS_KEY) || '{}');
    return map[usuarioId];
  } catch (error) {
    return undefined;
  }
};

const guardaClienteSeleccionadoId = (usuarioId, clienteId) => {
  try {
    const map = JSON.parse(window.localStorage.getItem(CLIENTES_SELECCIONADOS_KEY) || '{}');
    map[usuarioId] = clienteId;
    window.localStorage.setItem(CLIENTES_SELECCIONADOS_KEY, JSON.stringify(map));
  } catch (error) {
    // No bloqueamos el flujo si localStorage no está disponible.
  }
};

const borraClienteSeleccionadoId = (usuarioId) => {
  try {
    const map = JSON.parse(window.localStorage.getItem(CLIENTES_SELECCIONADOS_KEY) || '{}');
    delete map[usuarioId];
    window.localStorage.setItem(CLIENTES_SELECCIONADOS_KEY, JSON.stringify(map));
  } catch (error) {
    // ignore
  }
};

export const CuentaContext = React.createContext(cuentaContextInitialState);

export const useCuenta = () => {
  const contextState = React.useContext(CuentaContext);
  if (contextState === null) {
    throw new Error('useCuenta must be used within a CuentaProvider tag');
  }
  return contextState;
};

const CuentaProvider = ({ children, ...props }) => {

  const { app, actualizaApp } = useContext(AppContext);
  const [cuenta, setCuenta] = useState(cuentaInitialState);

  const [{ data: cuentaData, loading: loadingCuenta, error: cuentaError }, cuentaApi] = useApi(null, { defaultError: false });

  // Para reafirmar en el backend la instancia activa recordada (al recargar / re-login).
  const [{ error: activaError, response: activaResponse }, activaApi] = useApi(null, { defaultError: false });

  useEffect(() => {
    if (app.needUpdateSession === true) { //Se venció la sesion... a firmarse de nuevo
      setCuenta(cuentaInitialState);
      actualizaApp({ needUpdateSession: false });
    }
  }, [app]);

  useEffect(() => {
    if (cuenta.init !== true && cuenta.loaded !== true && loadingCuenta !== true) {
      cuentaApi(obtenerCuentas());
    }
  }, [cuenta]);

  useEffect(() => {
    if (cuentaError) {
      //cuentaError.response.status === 401 //Se hace algo??
      setCuenta({
        ...cuenta,
        init: true
      });
    }
  }, [cuentaError]);

  useEffect(() => {
    var _cuenta = {
      ...cuenta
    }
    if (cuentaData) {
      _cuenta = {
        ..._cuenta,
        init: true,
      }
      if (cuentaData.length > 0) {
        const usuario = cuentaData[0];
        const clientes = isArray(usuario.clientes) ? usuario.clientes : [];

        // La instancia activa se determina a partir del atributo `clientes`:
        // - varias instancias  -> se requiere selección; si hay una recordada se
        //                          reafirma en el backend (PUT /usuarios/cliente-activo).
        // - una sola instancia -> ya quedó activa en el login (viene en `cliente`).
        // - sin instancias     -> estado "sin instancias asignadas".
        let cliente = usuario.cliente;
        let requiereSeleccionCliente = false;
        let sinInstancias = false;
        // `false` significa "hay que reafirmarla en el backend"; `true` no requiere PUT.
        let clienteActivoConfirmado = true;

        if (clientes.length > 1) {
          const seleccionadoId = leeClienteSeleccionadoId(usuario.id);
          const encontrado = clientes.find(c => c.id === seleccionadoId);
          if (encontrado) {
            cliente = encontrado;
            clienteActivoConfirmado = false;
          } else {
            cliente = undefined;
            requiereSeleccionCliente = true;
          }
        } else if (clientes.length === 1) {
          cliente = clientes[0];
        } else if (!cliente) {
          sinInstancias = true;
        }

        _cuenta = {
          ..._cuenta,
          ...usuario,
          cliente,
          requiereSeleccionCliente,
          sinInstancias,
          clienteActivoConfirmado,
          loaded: true,
          logged: true
        }
      }
      setCuenta({
        ..._cuenta,
      })
    }
  }, [cuentaData]);

  // Reafirma en el backend la instancia activa recordada (caso multi-instancia al cargar).
  useEffect(() => {
    if (cuenta.cliente && cuenta.clienteActivoConfirmado === false) {
      activaApi(establecerInstanciaActiva(cuenta.cliente.id));
    }
  }, [cuenta.cliente, cuenta.clienteActivoConfirmado]);

  useEffect(() => {
    if (activaResponse && activaResponse.status >= 200 && activaResponse.status < 300) {
      setCuenta(prev => ({ ...prev, clienteActivoConfirmado: true }));
    }
  }, [activaResponse]);

  useEffect(() => {
    if (activaError) {
      // 409 u otro: la instancia recordada ya no es válida -> volver a seleccionar.
      setCuenta(prev => {
        borraClienteSeleccionadoId(prev.id);
        return {
          ...prev,
          cliente: undefined,
          requiereSeleccionCliente: true,
          clienteActivoConfirmado: true,
        };
      });
    }
  }, [activaError]);

  const recargaCuenta = React.useCallback(async () => {
    await cuentaApi(obtenerCuentas());
  }, []);

  const actualizaCuenta = React.useCallback((value) => {
    if (value === undefined) {
      setCuenta(cuentaInitialState);
    } else {
      setCuenta({
        ...cuenta,
        ...value,
      });
    }
  }, []);

  // Fija la instancia con la que se continuará y la recuerda para este usuario.
  // Se invoca DESPUÉS de que el backend confirmó (204) la instancia activa.
  const seleccionaCliente = React.useCallback((cliente) => {
    if (!cliente) {
      return;
    }
    setCuenta(prev => {
      guardaClienteSeleccionadoId(prev.id, cliente.id);
      return {
        ...prev,
        cliente,
        requiereSeleccionCliente: false,
        clienteActivoConfirmado: true,
      };
    });
  }, []);

  // Vuelve a solicitar la selección de cliente (cambiar de cliente sin cerrar sesión).
  const solicitaCambioCliente = React.useCallback(() => {
    setCuenta(prev => ({
      ...prev,
      requiereSeleccionCliente: true,
    }));
  }, []);

  const tieneCuentaPermisos = React.useCallback((permisos = []) => {
    if (!isArray(permisos)) {
      permisos = [permisos];
    }
    if (permisos.length > 0 && cuenta && cuenta.permisos && cuenta.permisos.length > 0) {
      const cuentaPermisos = cuenta.permisos.filter(p => permisos.includes(p.codigo));
      return cuentaPermisos.length > 0;
    }
    return false;
  }, []);

  const wipeAllData = React.useCallback(() => {
    actualizaCuenta();
    actualizaApp();
  }, []);

  // La cuenta está resuelta cuando ya cargó y, en el caso multi-instancia con
  // selección recordada, el backend confirmó la instancia activa (PUT 204).
  const cuentaResuelta = cuenta.init === true && cuenta.clienteActivoConfirmado !== false;

  return (
    <Fragment>
      <CuentaContext.Provider value={{
        cuenta,
        actualizaCuenta,
        tieneCuentaPermisos,
        wipeAllData,
        recargaCuenta,
        seleccionaCliente,
        solicitaCambioCliente,
      }}>
        <Fragment>
          {cuentaResuelta && children}
          {(loadingCuenta || !cuentaResuelta) &&
            <SuspenseLoading />
          }
        </Fragment>
      </CuentaContext.Provider>
    </Fragment>
  )
}

export default CuentaProvider;